import dayjs from "dayjs";
import { ClassActionItem } from "@/types/feed";
import { AdgemOffer } from "@/types/offer";

export interface TransformFeedDataParams {
  listFeed: any[];
  suitList: any[];
  unclaimedList: any[];
  hotelReservationsList: any[];
  purchasedProductsList: any[];
  flightReservationsList: any[];
  adgemOffersList: AdgemOffer[];
  setExpiredClassActions: (_items: ClassActionItem[]) => void;
  setClaimedClassActions: (_items: ClassActionItem[]) => void;
}

export function transformFeedData({
  listFeed,
  suitList,
  unclaimedList,
  hotelReservationsList,
  purchasedProductsList,
  flightReservationsList,
  adgemOffersList,
  setExpiredClassActions,
  setClaimedClassActions,
}: TransformFeedDataParams): any[] {
  let updatedFeed = [...listFeed];

  // Update class action proof status
  for (const item of updatedFeed) {
    if (item.type === "classAction") {
      item.proof = Boolean(
        suitList.find((suit: any) => suit.id === item.id)?.proof
      );
    }
  }

  // Filter expired class actions
  const { activeFeed, expiredActions, claimedActions } =
    filterExpiredClassActions(updatedFeed);
  updatedFeed = activeFeed;
  setExpiredClassActions(expiredActions);
  setClaimedClassActions(claimedActions);

  // Replace unclaimed property with unclaimed money items
  updatedFeed = addUnclaimedMoney(updatedFeed, unclaimedList);

  // Add hotel reservations
  updatedFeed = addHotelReservations(updatedFeed, hotelReservationsList);

  // Add purchased products
  updatedFeed = addPurchasedProducts(updatedFeed, purchasedProductsList);

  // Add flight reservations
  updatedFeed = addFlightReservations(updatedFeed, flightReservationsList);

  // Add adgem offers
  updatedFeed = addAdgemOffers(updatedFeed, adgemOffersList);

  return updatedFeed.sort((a, b) => b.short - a.short);
}

function filterExpiredClassActions(feed: any[]) {
  const expiredActions: ClassActionItem[] = [];
  const claimedActions: ClassActionItem[] = [];

  const activeFeed = feed.filter((item: any) => {
    if (item.type === "classAction") {
      const daysLeft = dayjs(item.dueAt).diff(dayjs(), "day");
      if (item.claimed) {
        claimedActions.push(item);
        return false;
      }
      if (daysLeft <= 0) {
        expiredActions.push(item);
        return false;
      }
    }
    return true;
  });

  return { activeFeed, expiredActions, claimedActions };
}

function addUnclaimedMoney(feed: any[], unclaimedList: any[]) {
  const formattedUnclaimedItems = unclaimedList.map((unclaimedItem: any) => ({
    id: unclaimedItem.id || unclaimedItem.name,
    title: "Unclaimed money from " + unclaimedItem.businessName,
    ...unclaimedItem,
    type: "unclaimedMoney" as any,
    canClaim: true,
  }));

  return [...feed, ...formattedUnclaimedItems];
}

function addHotelReservations(feed: any[], hotelReservationsList: any[]) {
  if (hotelReservationsList.length === 0) {
    return feed;
  }

  const activeReservations = hotelReservationsList.filter(
    (reservation: any) =>
      reservation.currentStatus !== "approved" &&
      reservation.currentStatus !== "rejected"
  );

  // Sort reservations: ones with potentialSavings first (by savings amount desc)
  const sortedReservations = [...activeReservations].sort((a, b) => {
    const savingsA = a.potentialSavings || 0;
    const savingsB = b.potentialSavings || 0;
    return savingsB - savingsA;
  });

  const hotelReservationItems = sortedReservations.map((reservation: any) => ({
    ...reservation,
    type: "hotelReservation" as any,
    // Give high priority to reservations with savings so they appear at top of feed
    short: reservation.potentialSavings && reservation.potentialSavings > 0 ? 9999 : 0,
  }));

  return [...feed, ...hotelReservationItems];
}

function addPurchasedProducts(feed: any[], purchasedProductsList: any[]) {
  if (purchasedProductsList.length === 0) {
    return feed;
  }

  const activeProducts = purchasedProductsList.filter(
    (product: any) =>
      product.currentStatus !== "approved" &&
      product.currentStatus !== "rejected"
  );

  const purchasedProductItems = activeProducts.map((product: any) => ({
    ...product,
    type: "purchasedProduct" as any,
  }));

  return [...feed, ...purchasedProductItems];
}

function addFlightReservations(feed: any[], flightReservationsList: any[]) {
  if (flightReservationsList.length === 0) {
    return feed;
  }

  // Only show flights with delays that qualify for compensation (3+ hours)
  const activeFlights = flightReservationsList.filter(
    (flight: any) =>
      flight.currentStatus !== "approved" &&
      flight.currentStatus !== "rejected" &&
      flight.delayNotice &&
      flight.delayNotice.delayMinutes >= 180
  );

  const flightReservationItems = activeFlights.map((flight: any) => ({
    ...flight,
    type: "flightReservation" as any,
  }));

  return [...feed, ...flightReservationItems];
}

function addAdgemOffers(feed: any[], adgemOffersList: AdgemOffer[]) {
  if (adgemOffersList.length === 0) {
    return feed;
  }

  const adgemOfferItems = adgemOffersList.map((offer: AdgemOffer) => ({
    ...offer,
    type: "adgemOffer" as any,
  }));

  return [...feed, ...adgemOfferItems];
}

function addUnclaimedProperty(feed: any[], unclaimedList: any[]) {
  if (unclaimedList.length === 0) {
    return feed;
  }

  const unclaimedPropertyItems = unclaimedList.map((unclaimedItem: any) => ({
    ...unclaimedItem,
  }));

  return [...feed, ...unclaimedPropertyItems];
}
